import TextLockupV2 from "components/elements/textLockupV2"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import PropTypes from "prop-types"
import React, { useEffect, useRef } from "react"
import "./section-split-steps-up-v2.scss"

// Register GSAP plugins
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger)
}

const SectionSplitStepsUpV2 = ({ steps, text, title }) => {
  const sectionRef = useRef(null)
  const stepRefs = useRef([])
  const hasAnimated = useRef(false)

  useEffect(() => {
    if (!sectionRef.current || !stepRefs.current.length) return

    const currentSection = sectionRef.current

    // Set initial state - scale from bottom up and hide content
    gsap.set(stepRefs.current, {
      scaleY: 0,
      transformOrigin: "bottom center",
    })

    // Set initial state for content inside each step
    stepRefs.current.forEach(step => {
      const content = step.querySelectorAll(
        ".section-split-steps-up-v2--step-icon, .section-split-steps-up-v2--step-title, .section-split-steps-up-v2--step-text"
      )
      gsap.set(content, {
        opacity: 0,
        y: 10, // Slight upward offset for smoother fade-in
      })
    })

    // Set initial state for arrows
    const arrow1 = currentSection.querySelector(
      ".section-split-steps-up-v2--step-arrow-1"
    )
    const arrow2 = currentSection.querySelector(
      ".section-split-steps-up-v2--step-arrow-2"
    )

    if (arrow1 && arrow2) {
      gsap.set([arrow1, arrow2], {
        opacity: 0,
        scale: 0.8,
      })
    }

    // Create the scroll-triggered animation
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: currentSection,
        start: "top 80%",
        end: "bottom 20%",
        toggleActions: "play none none none", // Only play once, no reverse
        once: true, // Animation only happens once
        onRefresh: () => {
          // Only reset if the animation hasn't played yet
          // If it has played, keep the final state
          if (!hasAnimated.current) {
            stepRefs.current.forEach(step => {
              gsap.set(step, { clearProps: "all" })
              gsap.set(step, {
                scaleY: 0,
                transformOrigin: "bottom center",
              })
            })
          }
        },
        onComplete: () => {
          hasAnimated.current = true
        },
      },
    })

    // Animate each step with staggered delays
    stepRefs.current.forEach((step, index) => {
      const delay = 0.2 + index * 0.2 // 0.2s, 0.4s, 0.6s delays
      const content = step.querySelectorAll(
        ".section-split-steps-up-v2--step-icon, .section-split-steps-up-v2--step-title, .section-split-steps-up-v2--step-text"
      )

      // Scale the bar first
      tl.to(
        step,
        {
          scaleY: 1,
          duration: 0.8,
          ease: "power2.out",
          delay: delay,
        },
        0
      )

      // Then fade in the content after the bar is scaled
      tl.to(
        content,
        {
          opacity: 1,
          y: 0,
          duration: 0.4,
          ease: "power2.out",
          delay: delay + 0.3, // Start content fade 0.3s after bar starts scaling
        },
        0
      )
    })

    // Animate arrows after all columns have scaled and content has faded
    if (arrow1 && arrow2) {
      // First arrow appears after third column completes (0.6 + 0.8 + 0.4 = 1.8s)
      tl.to(
        arrow1,
        {
          opacity: 1,
          scale: 1,
          duration: 0.6,
          ease: "power2.out",
          delay: 1.8, // Start after third column content completes
        },
        0
      )

      // Second arrow appears after first arrow completes
      tl.to(
        arrow2,
        {
          opacity: 1,
          scale: 1,
          duration: 0.6,
          ease: "power2.out",
          delay: 2.4, // Start after first arrow completes (1.8 + 0.6)
        },
        0
      )
    }

    // Add callback to timeline to mark animation as complete
    tl.call(() => {
      hasAnimated.current = true
    })

    // Handle window resize to recalculate heights
    const handleResize = () => {
      ScrollTrigger.refresh()

      // If animation has completed, ensure steps remain visible
      if (hasAnimated.current) {
        stepRefs.current.forEach(step => {
          gsap.set(step, {
            scaleY: 1,
            transformOrigin: "bottom center",
          })
        })
      }
    }

    window.addEventListener("resize", handleResize)

    // Cleanup function
    return () => {
      window.removeEventListener("resize", handleResize)
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.trigger === currentSection) {
          trigger.kill()
        }
      })
    }
  }, [steps])

  // Add ref to each step element
  const addToRefs = el => {
    if (el && !stepRefs.current.includes(el)) {
      stepRefs.current.push(el)
    }
  }

  return (
    <section className="section-split-steps-up-v2" ref={sectionRef}>
      <div className="container-v2">
        <div className="section-split-steps-up-v2--content">
          {title && (
            <h2 className="section-split-steps-up-v2--heading">
              <TextLockupV2>{title}</TextLockupV2>
            </h2>
          )}

          {text?.childrenMarkdownRemark?.[0]?.html && (
            <div
              className="section-split-steps-up-v2--text"
              dangerouslySetInnerHTML={{
                __html: text.childrenMarkdownRemark[0].html,
              }}
            />
          )}
        </div>

        <div className="section-split-steps-up-v2--steps-wrapper">
          <div className="section-split-steps-up-v2--steps">
            {steps?.[0] && (
              <div className="section-split-steps-up-v2--step" ref={addToRefs}>
                {steps[0].icon?.file?.url && (
                  <div className="section-split-steps-up-v2--step-icon">
                    <img alt={steps[0].title} src={steps[0].icon.file.url} />
                  </div>
                )}
                {steps[0].title && (
                  <h3 className="section-split-steps-up-v2--step-title">
                    {steps[0].title}
                  </h3>
                )}
                {steps[0].text && (
                  <p className="section-split-steps-up-v2--step-text">
                    {steps[0].text}
                  </p>
                )}

                <svg
                  className="section-split-steps-up-v2--step-arrow-1"
                  fill="none"
                  height="88"
                  viewBox="0 0 141 88"
                  width="141"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M138.229 25.481a2.295 2.295 0 0 0 2.176-2.408L139.36 2.447a2.296 2.296 0 0 0-4.584.232l.929 18.335-18.335.928a2.296 2.296 0 0 0 .233 4.584zM7.589 83.461q0-2.214.117-4.397l-4.583-.244A87 87 0 0 0 3 83.462zm1.058-13.135a80 80 0 0 1 1.888-8.578l-4.422-1.231a85 85 0 0 0-1.995 9.07zm4.707-16.892a79 79 0 0 1 3.723-7.95l-4.041-2.175a84 84 0 0 0-3.94 8.413zm8.308-15.438a79 79 0 0 1 5.39-6.928l-3.46-3.016a83 83 0 0 0-5.704 7.333zm11.52-13.21a78 78 0 0 1 6.795-5.552l-2.698-3.713a82 82 0 0 0-7.198 5.88zm14.174-10.298a77 77 0 0 1 7.87-3.872L53.44 6.389a81 81 0 0 0-8.343 4.104zm16.13-6.814a76 76 0 0 1 8.546-1.975l-.775-4.524a81 81 0 0 0-9.059 2.094zm17.266-2.961a78 78 0 0 1 4.392-.123V0q-2.34 0-4.648.13zm4.392-.123q2.378.001 4.711.146l.286-4.58A80 80 0 0 0 85.144 0zm14.05 1.325a74 74 0 0 1 9.111 2.359l1.432-4.361a78 78 0 0 0-9.679-2.506zm17.856 5.853a78 78 0 0 1 8.257 4.55l2.453-3.878a83 83 0 0 0-8.744-4.819zm15.93 10.052a85 85 0 0 1 3.594 3.072l3.077-3.405a90 90 0 0 0-3.788-3.237z"
                    fill="#122f4e"
                  />
                  <circle cx="5" cy="83" fill="#122f4e" r="5" />
                </svg>
              </div>
            )}

            {steps?.[1] && (
              <div className="section-split-steps-up-v2--step" ref={addToRefs}>
                {steps[1].icon?.file?.url && (
                  <div className="section-split-steps-up-v2--step-icon">
                    <img alt={steps[1].title} src={steps[1].icon.file.url} />
                  </div>
                )}
                {steps[1].title && (
                  <h3 className="section-split-steps-up-v2--step-title">
                    {steps[1].title}
                  </h3>
                )}
                {steps[1].text && (
                  <p className="section-split-steps-up-v2--step-text">
                    {steps[1].text}
                  </p>
                )}
                <svg
                  className="section-split-steps-up-v2--step-arrow-2"
                  fill="none"
                  height="88"
                  viewBox="0 0 141 88"
                  width="141"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M138.229 25.481a2.295 2.295 0 0 0 2.176-2.408L139.36 2.447a2.296 2.296 0 0 0-4.584.232l.929 18.335-18.335.928a2.296 2.296 0 0 0 .233 4.584zM7.589 83.461q0-2.214.117-4.397l-4.583-.244A87 87 0 0 0 3 83.462zm1.058-13.135a80 80 0 0 1 1.888-8.578l-4.422-1.231a85 85 0 0 0-1.995 9.07zm4.707-16.892a79 79 0 0 1 3.723-7.95l-4.041-2.175a84 84 0 0 0-3.94 8.413zm8.308-15.438a79 79 0 0 1 5.39-6.928l-3.46-3.016a83 83 0 0 0-5.704 7.333zm11.52-13.21a78 78 0 0 1 6.795-5.552l-2.698-3.713a82 82 0 0 0-7.198 5.88zm14.174-10.298a77 77 0 0 1 7.87-3.872L53.44 6.389a81 81 0 0 0-8.343 4.104zm16.13-6.814a76 76 0 0 1 8.546-1.975l-.775-4.524a81 81 0 0 0-9.059 2.094zm17.266-2.961a78 78 0 0 1 4.392-.123V0q-2.34 0-4.648.13zm4.392-.123q2.378.001 4.711.146l.286-4.58A80 80 0 0 0 85.144 0zm14.05 1.325a74 74 0 0 1 9.111 2.359l1.432-4.361a78 78 0 0 0-9.679-2.506zm17.856 5.853a78 78 0 0 1 8.257 4.55l2.453-3.878a83 83 0 0 0-8.744-4.819zm15.93 10.052a85 85 0 0 1 3.594 3.072l3.077-3.405a90 90 0 0 0-3.788-3.237z"
                    fill="#122f4e"
                  />
                  <circle cx="5" cy="83" fill="#122f4e" r="5" />
                </svg>
              </div>
            )}

            {steps?.[2] && (
              <div className="section-split-steps-up-v2--step" ref={addToRefs}>
                {steps[2].icon?.file?.url && (
                  <div className="section-split-steps-up-v2--step-icon">
                    <img alt={steps[2].title} src={steps[2].icon.file.url} />
                  </div>
                )}
                {steps[2].title && (
                  <h3 className="section-split-steps-up-v2--step-title">
                    {steps[2].title}
                  </h3>
                )}
                {steps[2].text && (
                  <p className="section-split-steps-up-v2--step-text">
                    {steps[2].text}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

SectionSplitStepsUpV2.propTypes = {
  steps: PropTypes.array.isRequired,
  text: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
}

export default SectionSplitStepsUpV2
